import { Header, QRCodeButton, Text } from '_/components';
import ContainerWidthLimit from '_/components/ContainerWidthLimit';
import { colors, fontSizes } from '_/config/theme';
import { useCustomization } from '_/hooks/CustomizationContext';
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { AppRoute, VisitFlowTypesProps } from '_/navigation/types';
import { VisitorCheckinModals } from '_/components/VisitorCheckinModals';
import { useNextCheckinScreen } from '_/hooks/useNextCheckinScreen';
import useVisitorCheckinStore from '_/components/VisitorCheckinModals/useVisitorCheckinStore';
import { useCentralizedValuesFallback } from '_/hooks/useCentralizedValuesFallback';
import { useChildrenCompanies } from '_/hooks/ChildrenCompaniesContex';
import { useTranslation } from 'react-i18next';
import { Lang, VisitFlowTypesModel } from '@w3lcome/types';
import { useEffect } from 'react';

const VisitFlowTypesScreen: React.FC = () => {
  const { setSelectedVisitFlowType, company } = useCustomization();

  const { navigate } = useNavigation<VisitFlowTypesProps>();

  const { goToNextScreen } = useNextCheckinScreen(AppRoute.CHECKIN);

  const { i18n } = useTranslation();

  const [setQRCodeReaderVisible] = useVisitorCheckinStore((state) => [
    state.setQRCodeReaderVisible,
  ]);

  const {
    setSelectedChildrenVisitFlowType,
    selectedCompanyId,
    setSelectedCompanyId,
    selectedChildrenVisitFlowType,
  } = useChildrenCompanies();

  const {
    selectedOrFallbackCustomization,
    selectedOrFallbackVisitFlowTypes,
    shouldSkipVisitFlowTypesScreen,
  } = useCentralizedValuesFallback();

  function localizedName(visitFlowType: VisitFlowTypesModel) {
    if (!visitFlowType?.placeholder || Object.keys(visitFlowType?.placeholder ?? {}).length === 0) {
      return visitFlowType.name;
    }

    const localizedPlaceholder = visitFlowType.placeholder?.[i18n.language];

    if (localizedPlaceholder) {
      return localizedPlaceholder;
    }

    const existingPlaceholder = Object.keys(visitFlowType.placeholder);

    return visitFlowType.placeholder[Lang.PTBR] || existingPlaceholder[0];
  }

  function handleSelectVisitFlowType(visitFlowType: VisitFlowTypesModel) {
    if (visitFlowType.companyId !== company?.id) {
      setSelectedChildrenVisitFlowType(visitFlowType);
      navigate(AppRoute.CHECKIN);
      return;
    }

    setSelectedVisitFlowType(visitFlowType);
    navigate(AppRoute.CHECKIN);
  }

  const returnRoute = () => {
    if (selectedChildrenVisitFlowType) setSelectedChildrenVisitFlowType(null);
    if (selectedCompanyId) setSelectedCompanyId(undefined);
  };

  useEffect(() => {
    if (shouldSkipVisitFlowTypesScreen && !selectedChildrenVisitFlowType) {
      handleSelectVisitFlowType(selectedOrFallbackVisitFlowTypes[0]);
    }
  }, []);

  if (shouldSkipVisitFlowTypesScreen) {
    return <></>;
  }

  return (
    <>
      <KeyboardAvoidingView
        enabled
        style={StyleSheet.absoluteFill}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <View style={styles.container}>
          <ContainerWidthLimit style={{ alignItems: 'center' }}>
            <Header onGoBack={returnRoute} />

            <View style={styles.content}>
              <FlatList
                data={selectedOrFallbackVisitFlowTypes}
                keyExtractor={(item) => item.id.toString()}
                scrollEventThrottle={16}
                scrollEnabled
                showsVerticalScrollIndicator={false}
                contentContainerStyle={{
                  gap: 16,
                }}
                renderItem={({ item }) => {
                  return (
                    <TouchableOpacity
                      key={item.id}
                      style={styles.itemContainer}
                      onPress={() => handleSelectVisitFlowType(item)}
                    >
                      <Text size={fontSizes.lg2} fontType="bold">
                        {localizedName(item)}
                      </Text>
                    </TouchableOpacity>
                  );
                }}
              />
              <View style={styles.footer}>
                <View style={styles.lineHorizontal} />

                {selectedOrFallbackCustomization?.qrCodeCheckinEnabled &&
                  !selectedOrFallbackCustomization.qrCodeCheckinAtHomeEnabled && (
                    <QRCodeButton
                      onPress={() => {
                        setQRCodeReaderVisible(true);
                      }}
                    />
                  )}
              </View>
            </View>
          </ContainerWidthLimit>
        </View>
      </KeyboardAvoidingView>

      <VisitorCheckinModals meetingModalVisible={false} goToNextScreen={goToNextScreen} />
    </>
  );
};

export default VisitFlowTypesScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
    alignItems: 'center',
  },
  content: {
    flex: 1,
    width: '60%',
  },
  lineHorizontal: {
    marginVertical: 16,
    backgroundColor: colors.light2,
    height: 1,
    width: '100%',
  },
  itemContainer: {
    padding: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.dark1,
    borderRadius: 4,
  },
  button: {
    marginVertical: 12,
  },
  footer: {
    paddingVertical: 16,
  },
});
